import React, { useContext, useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import backgroundImage from "../../../../images/background/mri_scanner_blue_overlay.jpg";
import LineChartIcon from "@rsuite/icons/LineChart";
import TableColumnIcon from "@rsuite/icons/TableColumn";
import DeviceIcon from "@rsuite/icons/Device";
import NoticeIcon from "@rsuite/icons/Notice";
import { DarkModeContext } from "../../../../appContexts/DarkModeContext";
import { Button, colors, gradients, shadows } from "../../../ui";

export const WelcomeFC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    // Trigger staggered card animations
    const timer = setTimeout(() => setCardsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: LineChartIcon,
      title: "Real-time Analytics",
      description: "Advanced charting and data visualization",
      delay: 0,
    },
    {
      icon: TableColumnIcon,
      title: "Data Management",
      description: "Comprehensive machine data tables",
      delay: 100,
    },
    {
      icon: NoticeIcon,
      title: "Smart Alerts",
      description: "Intelligent monitoring and notifications",
      delay: 200,
    },
    {
      icon: DeviceIcon,
      title: "Universal Access",
      description: "Monitor from anywhere, any device",
      delay: 300,
    },
  ];

  return (
    <WelcomeSC>
      <BackgroundOverlay />

      <ContentWrapper>
        <HeroSection>
          <WelcomeTitle>
            Welcome to <BrandName>Avante Remote Services</BrandName>
          </WelcomeTitle>
          <Tagline>
            Experience next-generation medical device monitoring with our
            advanced platform
          </Tagline>
        </HeroSection>

        <FeaturesSection>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                visible={cardsVisible}
                delay={feature.delay}
              >
                <IconWrapper>
                  <feature.icon />
                </IconWrapper>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>
      </ContentWrapper>
    </WelcomeSC>
  );
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Main container - Full viewport
const WelcomeSC = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  margin: -10px 0 0 0; /* Negative margin to account for MainSC padding-top */
  overflow-y: hidden;
  background: url(${backgroundImage}) no-repeat center center;
  background-size: cover;
  background-attachment: fixed;

  @media screen and (max-width: 768px) {
    margin: -10px 0 0 0; /* Account for mobile padding */
  }
`;

// Background overlay for better contrast
const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
  background: linear-gradient(
    135deg,
    rgba(28, 58, 95, 0.92) 0%,
    rgba(44, 90, 160, 0.88) 50%,
    rgba(28, 58, 95, 0.92) 100%
  );
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 50%,
      transparent 80%,
      rgba(0, 0, 0, 0.3) 100%
    );
  }
`;

// Content wrapper for centering
const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  gap: 3rem;

  @media screen and (max-width: 768px) {
    padding: 2rem 1rem;
    gap: 2rem;
  }
`;

// Hero section
const HeroSection = styled.div`
  text-align: center;
  max-width: 800px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const WelcomeTitle = styled.h1`
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 1rem 0;
  line-height: 1.3;
  letter-spacing: 0.5px;
`;

const BrandName = styled.span`
  display: block;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: #ffffff;
  margin-top: 0.5rem;
`;

const Tagline = styled.p`
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-weight: 300;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

// Features section
const FeaturesSection = styled.div`
  width: 100%;
  max-width: 1100px;
  animation: ${fadeInScale} 0.8s ease-out 0.3s both;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  width: 100%;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? 0 : "30px")});
  transition-delay: ${(props) => props.delay}ms;

  &:hover {
    transform: translateY(-8px);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(74, 144, 226, 0.4);
    box-shadow:
      0 12px 40px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(74, 144, 226, 0.2);
  }

  @media screen and (max-width: 768px) {
    padding: 1.5rem;
    flex-direction: row;
    text-align: left;
    gap: 1rem;
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 16px;
  background: ${gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: ${shadows.primary};
  transition: all 0.3s ease;

  svg {
    font-size: 2rem;
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  ${FeatureCard}:hover & {
    animation: ${float} 2s ease-in-out infinite;
    box-shadow:
      ${shadows.primary},
      0 0 30px rgba(74, 144, 226, 0.5);
  }

  @media screen and (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin-bottom: 0;
    flex-shrink: 0;

    svg {
      font-size: 1.75rem;
    }
  }
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const FeatureTitle = styled.h3`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.3px;

  @media screen and (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeatureDescription = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  font-weight: 300;

  @media screen and (max-width: 768px) {
    font-size: 0.9rem;
  }
`;
